import { useQuery } from '@tanstack/react-query';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { ChartCard } from './chart-card';

function cellColor(count: number): string {
  if (count === 0) return 'bg-muted';
  if (count <= 2) return 'bg-destructive/30';
  if (count <= 5) return 'bg-destructive/60';
  return 'bg-destructive';
}

interface AlertHeatmapProps {
  department?: string;
}

export function AlertHeatmap({ department }: AlertHeatmapProps) {
  const { data } = useQuery({
    queryKey: ['dashboard-heatmap', department ?? null],
    queryFn: () => api.getAlertHeatmap(35, department),
  });

  const today = new Date();
  const days: { date: string; count: number }[] = [];
  for (let i = 34; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86_400_000);
    const key = d.toISOString().slice(0, 10);
    const row = data?.rows.find((r) => r.date === key);
    days.push({ date: key, count: row?.open_count ?? 0 });
  }

  return (
    <ChartCard title="Alert activity" description="Last 35 days" contentClassName="px-4 pb-5">
      <TooltipProvider delayDuration={0}>
        <div className="grid grid-cols-7 gap-1.5">
          {days.map((d) => (
            <Tooltip key={d.date}>
              <TooltipTrigger asChild>
                <div
                  className={`aspect-square rounded-sm transition-transform hover:scale-110 ${cellColor(d.count)}`}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <span className="font-medium">{d.date}</span>
                {' — '}
                {d.count === 0 ? 'No alerts' : `${d.count} alert${d.count > 1 ? 's' : ''}`}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>

      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-muted" /> 0
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-destructive/30" /> 1–2
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-destructive/60" /> 3–5
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-destructive" /> 6+
        </span>
      </div>
    </ChartCard>
  );
}
